import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { Song } from "../../model/launchpad";
import SongPlatformsSheet from "./SongPlatformsSheet";

// Stands in for the app's Navigator: in iOS, SongTileButton calls
// navigator.present(sheet: .songPlatforms(song)) and the sheet is presented
// by whatever is hosting the navigator.
//
// Here the provider owns the sheet, and useSongPlatforms() lets any component
// present it, so tapping a song opens the same sheet from anywhere on the site
// without each page wiring up its own state.

const SongPlatformsContext = createContext<((song: Song) => void) | null>(null);

export default function SongPlatformsProvider(props: { children: React.ReactNode }) {
  const [song, setSong] = useState<Song | null>(null);

  const present = useCallback((next: Song) => setSong(next), []);
  const dismiss = useCallback(() => setSong(null), []);

  // Stable so consumers don't re-render every time the sheet opens or closes.
  const value = useMemo(() => present, [present]);

  return <SongPlatformsContext.Provider value={value}>
    {props.children}
    <SongPlatformsSheet song={song} onClose={dismiss} />
  </SongPlatformsContext.Provider>
}

/**
 * Presents the streaming-platforms sheet for a song.
 *
 * Returns null outside a provider, which lets components fall back to their own
 * handler rather than crashing — the component gallery relies on that.
 */
export function useSongPlatforms(): ((song: Song) => void) | null {
  return useContext(SongPlatformsContext);
}
